import { useState, useEffect } from 'react';
import {
  User as FirebaseUser,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { 
  auth,
  callGetUserDetails,
  callRegenerateApiKey,
  signInWithTwitter,
} from '@/lib/firebase';

// Local profile derived from Firebase Auth (for UI purposes)
export interface AuthProfile {
  userId: string;
  email: string | null;
  displayName: string;
  photoUrl: string | null;
}

export type AuthStatus = 'idle' | 'signing_in' | 'loading' | 'error';

interface UseAuthResult {
  user: FirebaseUser | null;
  profile: AuthProfile | null;
  agentName: string | null; // User's claimed agent name (null if not claimed)
  loading: boolean;
  error: Error | null;
  authStatus: AuthStatus;
  signInWithX: () => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
  regenerateApiKey: () => Promise<string>;
  refreshUserDetails: () => Promise<void>;
}

export function useAuth(): UseAuthResult {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<AuthProfile | null>(null);
  const [agentName, setAgentName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>('idle');

  // Fetch user details from backend (includes agentName if claimed)
  const fetchUserDetails = async () => {
    try {
      const result = await callGetUserDetails();
      setAgentName(result.data.agentName);
    } catch (err) {
      console.error('Failed to fetch user details:', err);
      // Don't throw - user can still use the app without agent info
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(
      auth,
      async (firebaseUser) => {
        setUser(firebaseUser);
        
        if (firebaseUser) {
          // Build profile from Firebase Auth data
          setProfile({
            userId: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName || 'User',
            photoUrl: firebaseUser.photoURL,
          });
          
          // Fetch user details (including agent) from backend
          await fetchUserDetails();
          
          setAuthStatus('idle');
        } else {
          setProfile(null);
          setAgentName(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Auth state change error:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribeAuth();
  }, []);

  const signInWithX = async () => {
    try {
      setError(null);
      setAuthStatus('signing_in');
      await signInWithTwitter();
    } catch (err) {
      setError(err as Error);
      setAuthStatus('error');
      throw err;
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
      setProfile(null);
      setAgentName(null);
      setAuthStatus('idle');
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const clearError = () => {
    setError(null);
    setAuthStatus('idle');
  };

  // Regenerate API key for the user's claimed agent
  const regenerateApiKey = async (): Promise<string> => {
    try {
      setError(null);
      const result = await callRegenerateApiKey();
      return result.data.apiKey;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  // Manually refresh user details (e.g., after claiming an agent)
  const refreshUserDetails = async () => {
    setAuthStatus('loading');
    await fetchUserDetails();
    setAuthStatus('idle');
  };

  return {
    user,
    profile,
    agentName,
    loading,
    error,
    authStatus,
    signInWithX,
    signOut,
    clearError,
    regenerateApiKey,
    refreshUserDetails,
  };
}
